import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import UniversalButton from "components/uiComponents/UniversalButton";
import "./DownloadReportModal.scss";

const DownloadReportModal = ({ onClose, onDownload }) => {
  const { t } = useTranslation();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      if (file.size <= 10 * 1024 * 1024) {
        setLogoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDownload = () => {
    onDownload(logoFile);
    onClose();
  };

  const handleDownloadWithoutLogo = () => {
    onDownload(null);
    onClose();
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="download-report-modal">
      <div className="download-report-modal__header">
        <h2 className="download-report-modal__title">
          {t("download_report", "Download report")}
        </h2>
        <p className="download-report-modal__subtitle">
          {t("preparing_report", "Preparing your report for download.")}
        </p>
      </div>

      <div className="download-report-modal__logo-section">
        <h3 className="download-report-modal__logo-title">
          {t("company_logo", "Company's logo")}{" "}
          <span className="download-report-modal__optional">
            ({t("optional", "optional")})
          </span>
        </h3>
        <p className="download-report-modal__logo-description">
          {t("add_logo_description", "Add your company logo to the report header.")}
        </p>

        <div
          className="download-report-modal__upload-zone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleBrowse}
        >
          {logoPreview ? (
            <div className="download-report-modal__preview">
              <img src={logoPreview} alt="Logo preview" />
              <div className="download-report-modal__preview-actions">
                <button
                  className="download-report-modal__action-btn download-report-modal__action-btn--upload"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowse();
                  }}
                  title={t("change_logo", "Change logo")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25M5.25 6L9 2.25M9 2.25L12.75 6M9 2.25V11.25" stroke="#4E7D96" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  className="download-report-modal__action-btn download-report-modal__action-btn--remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                  title={t("remove_logo", "Remove logo")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M2.25 4.5H3.75H15.75" stroke="#4E7D96" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5M14.25 15V6.5C14.25 5.39543 13.3546 4.5 12.25 4.5H5.75C4.64543 4.5 3.75 5.39543 3.75 6.5V15C3.75 15.3978 3.90804 15.7794 4.18934 16.0607C4.47064 16.342 4.85218 16.5 5.25 16.5H12.75C13.1478 16.5 13.5294 16.342 13.8107 16.0607C14.092 15.7794 14.25 15.3978 14.25 15Z" stroke="#4E7D96" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M7.5 8.25V12.75" stroke="#4E7D96" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M10.5 8.25V12.75" stroke="#4E7D96" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="download-report-modal__upload-content">
              <svg 
                className="download-report-modal__upload-icon"
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 18 18" 
                fill="none"
              >
                <path 
                  d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25M5.25 6L9 2.25M9 2.25L12.75 6M9 2.25V11.25" 
                  stroke="#4E7D96" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
                <p className="download-report-modal__upload-text">
                  {t("drop_files_or", "Drop files or")}{" "}
                  <span className="download-report-modal__browse">
                    {t("browse", "browse")}
                  </span>
                </p>
              </div>
              <p className="download-report-modal__file-info">
                {t("file_format_info", "png or jpg files up to 10mb")}
              </p>
            </>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      <div className="download-report-modal__actions">
        <UniversalButton
          label={logoFile ? t("download_with_logo", "Download with logo") : t("download_without_logo", "Download without logo")}
          labelColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
          isFullWidth
          onClick={logoFile ? handleDownload : handleDownloadWithoutLogo}
        />
        <button
          className="download-report-modal__cancel"
          onClick={onClose}
        >
          {t("cancel", "Cancel")}
        </button>
      </div>
    </div>
  );
};

export default DownloadReportModal;
